import { debounce } from 'lodash';
import Router, { useRouter } from 'next/router';
import { memo, useCallback, useEffect, useRef, useState } from 'react';

import { type AirActionTrackingLocation } from '@air/analytics';
import { type SearchSavedFiltersResponse, type SuggestionsResponse } from '@air/api/types';
import { useBreakpointsContext } from '@air/provider-media-query';

import { type ItemTypeFilter } from '~/components/Filters/types';
import { PrivateFiltersBar } from '~/components/Filters/v2/components/PrivateFiltersBar';
import { usePrivateSearchUIHelpers } from '~/components/Search/hooks/usePrivateSearchUIHelpers';
import { type SearchBarProps } from '~/components/SearchBar/SearchBar';
import { type BoardSuggestionType } from '~/components/SearchBar/types';
import { MobileSearchCommand } from '~/components/SearchCommand/MobileSearchCommand';
import { SearchCommand, type SearchCommandProps } from '~/components/SearchCommand/SearchCommand';
import { useWorkspaceSidebarPushLock } from '~/components/v2/hooks/useWorkspaceSidebarPushLock';
import { Pathname } from '~/constants/pathname';
import { useAppliedFiltersCount } from '~/hooks/filters/useAppliedFiltersCount';
import { usePrivateSmartSearchSimilarToFilter } from '~/hooks/filters/usePrivateSmartSearchSimilarToFilter';
import { useSearchParams } from '~/hooks/search/useSearchParams';
import { useGoToBoardPage } from '~/hooks/useGoToBoardPage';
import { usePrivateSearchContext } from '~/providers/PrivateSearchProvider';

const SEARCH_OVERLAY_LOCK_TOKEN = 'search-command';
const SEARCH_DEBOUNCE_MS = 400;

export type BasePrivateSearchProps = {
  compact?: boolean;
  defaultOpen?: boolean;
  placeholder?: string;
  onCloseClick?: () => void;
  trackLocation: AirActionTrackingLocation | string;
  hideFilterBar?: boolean;
};

export type PrivateSearchProps = {
  availableFilterTypes?: ItemTypeFilter[];
  onFocusChange?: SearchBarProps['onFocusChange'];
  suggestions?: SuggestionsResponse;
  onSearchChange: SearchBarProps['onSearchTermChange'];
  onApplyTextSearch?: SearchBarProps['onApplyTextSearch'];
  hideFilterBar?: boolean;
} & BasePrivateSearchProps &
  Pick<SearchCommandProps, 'renderActions'>;

export const PrivateSearch = memo(
  ({
    availableFilterTypes,
    compact,
    defaultOpen,
    onCloseClick,
    placeholder,
    trackLocation,
    onFocusChange,
    suggestions,
    onSearchChange,
    onApplyTextSearch,
    renderActions,
    hideFilterBar = false,
  }: PrivateSearchProps) => {
    const { search, setSearch } = useSearchParams();
    const { filtersCount } = useAppliedFiltersCount();
    const { isAboveMediumScreen } = useBreakpointsContext();
    const { goToBoardPage } = useGoToBoardPage();
    const [hasOpenedFilters, setHasOpenedFilters] = useState(false);
    const { addSimilarToImageFilter, removeSimilarToImageFilter } = usePrivateSmartSearchSimilarToFilter();
    const { isActiveImageSearch } = usePrivateSearchUIHelpers();
    const { setIsSearchCommandOpen } = usePrivateSearchContext();
    const { handleOpenChange } = useWorkspaceSidebarPushLock({ token: SEARCH_OVERLAY_LOCK_TOKEN });

    const router = useRouter();

    const debouncedSearchChange = useCallback(
      (value: string) => {
        debounce((searchValue: string) => onSearchChange(searchValue), SEARCH_DEBOUNCE_MS)(value);
      },
      [onSearchChange],
    );

    const _onApplyTextSearch = useCallback(
      (newTerm: string) => {
        onApplyTextSearch?.(newTerm);
        setSearch(newTerm);
      },
      [onApplyTextSearch, setSearch],
    );

    const onBoardClick = useCallback(
      (board: BoardSuggestionType) => goToBoardPage({ board, trackLocation: trackLocation || 'search' }),
      [goToBoardPage, trackLocation],
    );

    const onSavedFilterClick = useCallback((savedFilter: SearchSavedFiltersResponse) => {
      return Router.push({
        pathname: Pathname.SavedSearchBySavedSearchId,
        query: {
          savedSearchId: savedFilter.id,
        },
      });
    }, []);

    const onSimilarImageSearch = useCallback(
      (base64Image: string) => {
        addSimilarToImageFilter(base64Image, 'search-command');
      },
      [addSimilarToImageFilter],
    );

    const previousPathnameRef = useRef(router.pathname);

    useEffect(() => {
      const handleRouteChange = (url: string) => {
        // Parse the URL to extract pathname (handles query params and hash properly)
        const urlObj = new URL(url, window.location.origin);
        const newPathname = urlObj.pathname;

        // Clear image search when navigating to a different path (not on query param changes)
        if (previousPathnameRef.current !== newPathname && isActiveImageSearch) {
          removeSimilarToImageFilter();
        }

        previousPathnameRef.current = newPathname;
      };

      router.events.on('routeChangeComplete', handleRouteChange);

      return () => {
        router.events.off('routeChangeComplete', handleRouteChange);
      };
    }, [isActiveImageSearch, removeSimilarToImageFilter, router.events]);

    const onOpenChange = useCallback(
      (open: boolean) => {
        if (open) {
          setHasOpenedFilters(false);
        }
        onFocusChange?.(open);
        setIsSearchCommandOpen(open);
        handleOpenChange(open);
      },
      [onFocusChange, setIsSearchCommandOpen, handleOpenChange],
    );

    const onOpenFilterChange = useCallback(() => {
      setHasOpenedFilters(true);
    }, []);

    if (isAboveMediumScreen) {
      return (
        <SearchCommand
          compact={compact}
          filterBar={
            isAboveMediumScreen &&
            !hideFilterBar && (
              <PrivateFiltersBar
                className="bg-grey-2"
                onOpenChange={onOpenFilterChange}
                trackLocation={trackLocation}
                availableFilterTypes={availableFilterTypes}
              />
            )
          }
          hideSuggestions={hasOpenedFilters}
          onBoardClick={onBoardClick}
          onClear={isAboveMediumScreen ? undefined : onCloseClick}
          onOpenChange={onOpenChange}
          onSavedFilterSelect={onSavedFilterClick}
          onSearchChange={debouncedSearchChange}
          onSuggestionClick={_onApplyTextSearch}
          onSimilarImageSearch={onSimilarImageSearch}
          placeholder={placeholder}
          renderActions={renderActions}
          search={search}
          suggestions={hasOpenedFilters ? undefined : suggestions}
        />
      );
    }

    return (
      <MobileSearchCommand
        defaultOpen={defaultOpen}
        filtersCount={isAboveMediumScreen ? filtersCount : undefined}
        onBoardClick={onBoardClick}
        onClear={isAboveMediumScreen ? undefined : onCloseClick}
        onOpenChange={onFocusChange}
        onSavedFilterSelect={onSavedFilterClick}
        onSearchChange={debouncedSearchChange}
        onSuggestionClick={_onApplyTextSearch}
        placeholder={placeholder}
        search={search}
        suggestions={suggestions}
      />
    );
  },
);

PrivateSearch.displayName = 'PrivateSearch';
